import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiDollarSign, FiCheck, FiX, FiClock, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import { getPlanById } from '@/config/planConfig';

interface Invoice {
  id: string;
  user_id: string;
  plan_id: string;
  amount_usd: number;
  crypto_currency: string;
  crypto_amount: number;
  payment_address: string;
  status: string;
  created_at: string;
  paid_at: string | null;
  tx_hash: string | null;
  user_email?: string;
}

const CryptoInvoicesPanel: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [txHashInput, setTxHashInput] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<'all' | 'pending' | 'paid' | 'expired'>('pending');

  useEffect(() => {
    fetchInvoices();
  }, [filter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('crypto_invoices')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      console.log('📄 Crypto invoices query result:', { data, error, filter });

      if (error) throw error;

      // Fetch user emails separately for each invoice
      const formattedInvoices = await Promise.all(
        (data || []).map(async (inv: any) => {
          // Try to get email from user_profiles
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('email')
            .eq('user_id', inv.user_id)
            .maybeSingle();
          
          return {
            ...inv,
            user_email: profile?.email || inv.user_id,
          };
        })
      );

      setInvoices(formattedInvoices);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async (invoiceId: string) => {
    setProcessing(invoiceId);
    try {
      const txHash = txHashInput[invoiceId] || null;
      const invoice = invoices.find((i) => i.id === invoiceId);

      const { data, error } = await supabase.rpc('admin_mark_invoice_paid', {
        p_invoice_id: invoiceId,
        p_tx_hash: txHash,
      });

      if (error) throw error;

      if (data?.success) {
        // Fire the same V3-branded emails Stripe users get. Otherwise
        // crypto payers were being silently onboarded/upgraded — no
        // subscription_created confirmation, no invoice_paid receipt,
        // no plan_upgraded/downgraded nudge. Non-blocking: if the
        // email send fails we still consider the payment processed;
        // we just log and move on.
        try {
          const eventType = (data as any).previous_plan
            ? ((data as any).is_upgrade === false
                ? 'plan_downgraded'
                : 'plan_upgraded')
            : 'subscription_created';
          await supabase.functions.invoke('send-billing-email', {
            body: {
              email:        (data as any).user_email || invoice?.user_email,
              userName:     null,
              type:         eventType,
              planId:       invoice?.plan_id,
              amount:       invoice?.amount_usd,
              billingType:  (data as any).billing_type || 'monthly',
              periodEnd:    (data as any).paid_until,
              previousPlan: (data as any).previous_plan || null,
            },
          });
          // Also fire the invoice_paid receipt so the payer gets both
          // "welcome to Pro" and "here's your receipt", same as Stripe.
          await supabase.functions.invoke('send-billing-email', {
            body: {
              email:       (data as any).user_email || invoice?.user_email,
              type:        'invoice_paid',
              planId:      invoice?.plan_id,
              amount:      invoice?.amount_usd,
              billingType: (data as any).billing_type || 'monthly',
            },
          });
        } catch (emailErr) {
          console.warn('[crypto] payment marked paid but email send failed:', emailErr);
        }

        fetchInvoices();
        setTxHashInput((prev) => {
          const newState = { ...prev };
          delete newState[invoiceId];
          return newState;
        });
      } else {
        alert(data?.error || 'Failed to mark as paid');
      }
    } catch (err: any) {
      console.error('Error marking invoice as paid:', err);
      alert(err.message || 'Error processing payment');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs font-medium rounded-full flex items-center gap-1">
            <FiCheck className="w-3 h-3" /> Paid
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs font-medium rounded-full flex items-center gap-1">
            <FiClock className="w-3 h-3" /> Pending
          </span>
        );
      case 'expired':
        return (
          <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs font-medium rounded-full flex items-center gap-1">
            <FiX className="w-3 h-3" /> Expired
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20">
            <FiDollarSign className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Crypto Invoices</h3>
            <p className="text-sm text-muted-foreground">Manage crypto payment invoices</p>
          </div>
        </div>
        <button
          onClick={fetchInvoices}
          disabled={loading}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
        >
          <FiRefreshCw className={`w-5 h-5 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(['all', 'pending', 'paid', 'expired'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
              filter === tab
                ? 'bg-gradient-to-r from-purple-600 to-cyan-600 text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No {filter !== 'all' ? filter : ''} invoices found
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-background/50 rounded-xl p-4 border border-border/30"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusBadge(invoice.status)}
                    <span className="text-sm text-muted-foreground">
                      {new Date(invoice.created_at).toLocaleString()}
                    </span>
                  </div>
                  
                  <p className="font-medium text-foreground mb-1 truncate">
                    {invoice.user_email || 'Unknown User'}
                  </p>
                  
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="text-muted-foreground">
                      Plan: <span className="text-foreground">{getPlanById(invoice.plan_id)?.name || invoice.plan_id}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Amount: <span className="text-foreground">${invoice.amount_usd}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Crypto: <span className="text-foreground">{invoice.crypto_amount} {invoice.crypto_currency}</span>
                    </span>
                  </div>

                  {invoice.tx_hash && (
                    <p className="text-xs text-muted-foreground mt-2 font-mono truncate">
                      TX: {invoice.tx_hash}
                    </p>
                  )}
                </div>

                {/* Actions for pending invoices */}
                {invoice.status === 'pending' && (
                  <div className="flex flex-col gap-2">
                    <input
                      type="text"
                      placeholder="TX Hash (optional)"
                      value={txHashInput[invoice.id] || ''}
                      onChange={(e) => setTxHashInput((prev) => ({ ...prev, [invoice.id]: e.target.value }))}
                      className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground w-40"
                    />
                    <button
                      onClick={() => markAsPaid(invoice.id)}
                      disabled={processing === invoice.id}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                    >
                      {processing === invoice.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <FiCheck className="w-4 h-4" />
                          Mark Paid
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CryptoInvoicesPanel;
